import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import {
  BehaviorSubject,
  Subject,
  combineLatest,
  map,
  startWith,
  takeUntil,
  tap
} from 'rxjs';
import {
  DriverTypeApiLabel,
  DriverTypeApiValue
} from 'src/app/enum/driver-type.enum';
import {
  AssignmentDriver,
  AssignationDriversModalResult,
  DriverScheduleMode
} from '../../models';

@Component({
  selector: 'app-assignation-drivers-modal',
  templateUrl: './assignation-drivers-modal.component.html',
  styleUrls: ['./assignation-drivers-modal.component.css']
})
export class AssignationDriversModalComponent implements OnInit, OnDestroy {
  @Input() drivers: AssignmentDriver[] = [];
  @Input() selectedDrivers: AssignmentDriver[] = [];

  readonly searchControl = new FormControl<string>('', { nonNullable: true });
  readonly operatorTypeControl = new FormControl<boolean | null>(null);
  readonly driverTypeControl = new FormControl<DriverTypeApiValue | null>(null);

  readonly operatorTypeOptions = [
    {
      labelKey: 'service_manager.gantt.driver_type_all',
      value: null as boolean | null
    },
    { labelKey: 'service_manager.gantt.operator_type_internal', value: true },
    { labelKey: 'service_manager.gantt.operator_type_external', value: false }
  ];

  readonly driverTypeOptions: Array<{
    labelKey: string;
    value: DriverTypeApiValue | null;
  }> = [
    { labelKey: 'service_manager.gantt.driver_type_all', value: null },
    ...(Object.values(DriverTypeApiValue) as DriverTypeApiValue[]).map(
      (value) => ({
        labelKey: DriverTypeApiLabel[value],
        value
      })
    )
  ];

  readonly drivers$ = new BehaviorSubject<AssignmentDriver[]>([]);

  readonly filteredDrivers$ = combineLatest([
    this.drivers$,
    this.searchControl.valueChanges.pipe(startWith(this.searchControl.value)),
    this.operatorTypeControl.valueChanges.pipe(
      startWith(this.operatorTypeControl.value)
    ),
    this.driverTypeControl.valueChanges.pipe(
      startWith(this.driverTypeControl.value)
    )
  ]).pipe(
    map(([drivers, searchTerm, operatorType, driverType]) =>
      this.filterDrivers(drivers, searchTerm ?? '', operatorType, driverType)
    ),
    tap((drivers) => {
      this.filteredDriversSnapshot = drivers;
      this.syncSelectAllState();
    })
  );

  readonly selectedDriverIds = new Set<string>();

  allVisibleSelected = false;

  private filteredDriversSnapshot: AssignmentDriver[] = [];
  private readonly destroy$ = new Subject<void>();

  constructor(public readonly activeModal: NgbActiveModal) {}

  ngOnInit(): void {
    const clonedDrivers = this.drivers.map((driver) =>
      this.cloneDriver(driver)
    );
    this.drivers$.next(clonedDrivers);

    const initialSelection =
      this.selectedDrivers.length > 0 ? this.selectedDrivers : clonedDrivers;

    initialSelection.forEach((driver) =>
      this.selectedDriverIds.add(driver.driverId)
    );

    this.operatorTypeControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (value !== true) {
          this.driverTypeControl.setValue(null, { emitEvent: false });
        }
      });

    this.filteredDrivers$.pipe(takeUntil(this.destroy$)).subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get showDriverTypeFilter(): boolean {
    return this.operatorTypeControl.value === true;
  }

  get includedDrivers(): number {
    return this.selectedDriverIds.size;
  }

  get totalDrivers(): number {
    return this.drivers$.value.length;
  }

  get filteredCount(): number {
    return this.filteredDriversSnapshot.length;
  }

  close(): void {
    this.activeModal.dismiss();
  }

  save(): void {
    const selectedDrivers = this.drivers$.value.filter((driver) =>
      this.selectedDriverIds.has(driver.driverId)
    );

    this.activeModal.close({ selectedDrivers });
  }

  clearSearch(): void {
    this.searchControl.setValue('');
  }

  clearOperatorType(): void {
    this.operatorTypeControl.setValue(null);
  }

  clearDriverType(): void {
    this.driverTypeControl.setValue(null);
  }

  clearAllFilters(): void {
    this.clearSearch();
    this.clearOperatorType();
    this.clearDriverType();
  }

  trackByDriverId(_: number, driver: AssignmentDriver): string {
    return driver.driverId;
  }

  isDriverSelected(driverId: string): boolean {
    return this.selectedDriverIds.has(driverId);
  }

  toggleDriverSelection(driverId: string, isSelected: boolean): void {
    if (isSelected) {
      this.selectedDriverIds.add(driverId);
    } else {
      this.selectedDriverIds.delete(driverId);
    }
    this.syncSelectAllState();
  }

  onToggleSelectAll(isSelected: boolean): void {
    this.filteredDriversSnapshot.forEach((driver) => {
      if (isSelected) {
        this.selectedDriverIds.add(driver.driverId);
      } else {
        this.selectedDriverIds.delete(driver.driverId);
      }
    });
    this.allVisibleSelected = isSelected;
  }

  changeScheduleMode(driverId: string, mode: DriverScheduleMode): void {
    const drivers = this.drivers$.value;
    const driverToUpdate = drivers.find((d) => d.driverId === driverId);

    if (driverToUpdate) {
      driverToUpdate.schedule.mode = mode;
      this.drivers$.next([...drivers]);
    }
  }

  getDriverTypeTranslationKey(type: DriverTypeApiValue | null): string {
    return DriverTypeApiLabel[type ?? DriverTypeApiValue.NOT_SPECIFIED];
  }

  private syncSelectAllState(): void {
    if (this.filteredDriversSnapshot.length === 0) {
      this.allVisibleSelected = false;
      return;
    }

    const allSelected = this.filteredDriversSnapshot.every((driver) =>
      this.selectedDriverIds.has(driver.driverId)
    );
    this.allVisibleSelected = allSelected;
  }

  private filterDrivers(
    drivers: AssignmentDriver[],
    searchTerm: string,
    operatorType: boolean | null,
    driverType: DriverTypeApiValue | null
  ): AssignmentDriver[] {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return drivers.filter((driver) => {
      const matchesSearch =
        !normalizedSearch ||
        driver.fullName.toLowerCase().includes(normalizedSearch);

      const matchesOperatorType =
        operatorType === null ? true : driver.isInternal === operatorType;

      const matchesDriverType =
        operatorType === true && driverType
          ? driver.driverType === driverType
          : true;

      return matchesSearch && matchesOperatorType && matchesDriverType;
    });
  }

  private cloneDriver(driver: AssignmentDriver): AssignmentDriver {
    return {
      ...driver,
      schedule: {
        mode: driver.schedule.mode,
        full: { ...driver.schedule.full },
        split: { ...driver.schedule.split }
      }
    };
  }

  onTimeInput(event: Event, driver: AssignmentDriver, field: string): void {
    const input = event.target as HTMLInputElement;
    const raw = (input.value || '').replace(/\D/g, '');

    if (raw.length < 3) {
      input.value = raw;
      return;
    }

    const formatted = `${raw.slice(0, 2)}:${raw.slice(2, 4)}`;
    const isValid = /^([01]\d|2[0-3]):([0-5]\d)$/.test(formatted);

    if (isValid) {
      input.value = formatted;
      this.updateDriverTime(driver, field, formatted);
    } else if (raw.length < 5) {
      input.value = raw;
    } else {
      input.value = '';
      this.updateDriverTime(driver, field, '');
    }
  }

  private updateDriverTime(driver: AssignmentDriver, field: string, value: string): void {
    const drivers = this.drivers$.value;
    const driverToUpdate = drivers.find((d) => d.driverId === driver.driverId);

    if (driverToUpdate) {
      if (driver.schedule.mode === 'full') {
        if (field === 'fullStart') driverToUpdate.schedule.full.start = value;
        if (field === 'fullEnd') driverToUpdate.schedule.full.end = value;
      } else {
        if (field === 'morningStart') driverToUpdate.schedule.split.morningStart = value;
        if (field === 'morningEnd') driverToUpdate.schedule.split.morningEnd = value;
        if (field === 'afternoonStart') driverToUpdate.schedule.split.afternoonStart = value;
        if (field === 'afternoonEnd') driverToUpdate.schedule.split.afternoonEnd = value;
      }
      this.drivers$.next([...drivers]);
    }
  }
}
